import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { ThemeContext } from '~/common/context/theme/theme-context'
import { UserPreferencesContext } from '~/common/context/user-preferences/user-preferences-context'
import { useCurrentUser } from '~/common/use-current-user'
import { useFeatures } from '~/common/use-features'
import { useUpdateUser } from '~/api/supabase-mutations'
import { cacheManager } from '~/api/cache-manager'
import NumberField from '~/components/number-field/NumberField'
import { useUserPreferenceTypes } from './use-user-preference-types'

export const UserPreferencesCard = () => {
  const { mode, resolvedMode, setMode } = useContext(ThemeContext)
  const { setUserPreferences } = useContext(UserPreferencesContext)
  const { currentUser } = useCurrentUser()
  const { features } = useFeatures()
  const { updateUser } = useUpdateUser()
  const { enqueueSnackbar } = useSnackbar()
  const { userPreferenceTypes } = useUserPreferenceTypes()

  const [systemPreference, setSystemPreference] = useState(() => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })
  // localFeatures: resolved feature map used for the admin editor (optimistic, object form)
  const [localFeatures, setLocalFeatures] = useState({})
  const [preferences, setPreferences] = useState([])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e) => setSystemPreference(e.matches ? 'dark' : 'light')
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    // Deprecated: addListener() fallback for older browsers.
    // Consider removing when dropping support for legacy browsers.
    } else if (typeof mediaQuery.addEventListener !== 'function' && typeof mediaQuery.addListener === 'function') {
      // Deprecated: addListener() fallback for older browsers.
      // We use addListener and removeListener as a fallback to support browsers that do
      // not yet implement addEventListener/removeEventListener on MediaQueryList.
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
  }, [])

  // Initialize localFeatures from resolved map on first load; re-sync after cache refresh
  useEffect(() => {
    function initFeatures() {
      if (Object.keys(features).length > 0) {
        setLocalFeatures(prev => Object.keys(prev).length === 0 ? { ...features } : prev)
      }
    }
    initFeatures()
  }, [features])

  useEffect(() => {
    function syncPreferences() {
      if (!currentUser) return
      setPreferences(currentUser.preferences || [])
    }
    syncPreferences()
  }, [currentUser])

  const isSuperAdmin = currentUser?.permissions === 1

  const saveFeatures = async (updatedFeatures) => {
    if (!currentUser) return
    await updateUser(currentUser.id, { features: updatedFeatures, updated_at: new Date() }, {
      onSuccess: () => {
        cacheManager.invalidate('users')
        cacheManager.invalidate('user_resolved_features')
        enqueueSnackbar('Feature updated', { variant: 'success' })
      },
      onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    })
  }

  const savePreferences = async (updatedPreferences) => {
    if (!currentUser) return
    await updateUser(currentUser.id, { preferences: updatedPreferences, updated_at: new Date() }, {
      onSuccess: () => {
        cacheManager.invalidate('users')
        const flat = Object.fromEntries(updatedPreferences.map(p => Object.entries(p)[0]))
        setUserPreferences(flat)
        enqueueSnackbar('Preference saved', { variant: 'success' })
      },
      onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    })
  }

  const handleFeatureChange = (key, checked) => {
    const updated = { ...localFeatures, [key]: checked }
    setLocalFeatures(updated)
    const asArray = Object.entries(updated).map(([k, v]) => ({ [k]: v }))
    saveFeatures(asArray)
  }

  const handlePreferenceChange = (key, rawValue) => {
    const existing = preferences.find(p => key in p)
    const originalValue = existing ? existing[key] : undefined
    const parsedValue = typeof originalValue === 'number' ? Number(rawValue) : rawValue
    if (parsedValue === originalValue) return
    const updated = preferences.map(p => (key in p ? { [key]: parsedValue } : p))
    if (!updated.find(p => key in p)) updated.push({ [key]: parsedValue })
    setPreferences(updated)
    savePreferences(updated)
  }

  return (
    <Card variant="outlined">
      <CardHeader title="User" sx={{ pb: 0 }} />
      <CardContent>
        <Stack spacing={3}>
          {/* Theme */}
          {features['theme_toggle'] && (
            <Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Theme
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  size="small"
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="system">System ({systemPreference})</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {mode === 'system'
                  ? `Following system preference (currently ${resolvedMode})`
                  : `Using ${resolvedMode} theme`}
              </Typography>
            </Box>
          )}

          {/* User preferences */}
          {preferences.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Preferences
                </Typography>
                <Stack spacing={1.5}>
                  {preferences.map((pref) => {
                    const [key, value] = Object.entries(pref)[0]
                    const description = userPreferenceTypes.find(t => t.code === key)?.description ?? ''
                    const isNumeric = typeof value === 'number'

                    let input
                    if (key === 'temperature_unit') {
                      input = (
                        <FormControl size="small">
                          <Select
                            value={value}
                            onChange={(e) => handlePreferenceChange(key, e.target.value)}
                            size="small"
                          >
                            <MenuItem value="°C">°C</MenuItem>
                            <MenuItem value="°F">°F</MenuItem>
                          </Select>
                        </FormControl>
                      )
                    } else if (key === 'distance_unit') {
                      input = (
                        <FormControl size="small">
                          <Select
                            value={value}
                            onChange={(e) => handlePreferenceChange(key, e.target.value)}
                            size="small"
                          >
                            <MenuItem value="m">m</MenuItem>
                            <MenuItem value="ft">ft</MenuItem>
                          </Select>
                        </FormControl>
                      )
                    } else if (key === 'area_unit') {
                      input = (
                        <FormControl size="small">
                          <Select
                            value={value}
                            onChange={(e) => handlePreferenceChange(key, e.target.value)}
                            size="small"
                          >
                            <MenuItem value="m²">m²</MenuItem>
                            <MenuItem value="ft²">ft²</MenuItem>
                            <MenuItem value="ac">ac</MenuItem>
                          </Select>
                        </FormControl>
                      )
                    } else if (key === 'mass_unit') {
                      input = (
                        <FormControl size="small">
                          <Select
                            value={value}
                            onChange={(e) => handlePreferenceChange(key, e.target.value)}
                            size="small"
                          >
                            <MenuItem value="kg">kg</MenuItem>
                            <MenuItem value="lbs">lbs</MenuItem>
                          </Select>
                        </FormControl>
                      )
                    } else if (isNumeric) {
                      input = (
                        <NumberField
                          label={key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                          value={value}
                          onValueChange={(val) => handlePreferenceChange(key, val)}
                          size="small"
                        />
                      )
                    } else {
                      input = (
                        <TextField
                          label={key.replace(/_/g, ' ')}
                          defaultValue={value}
                          size="small"
                          onBlur={(e) => handlePreferenceChange(key, e.target.value)}
                        />
                      )
                    }

                    return (
                      <Box
                        key={key}
                        sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, alignItems: 'center' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {description}
                        </Typography>
                        {input}
                      </Box>
                    )
                  })}
                </Stack>
              </Box>
            </>
          )}

          {/* Feature flags */}
          {isSuperAdmin && Object.keys(localFeatures).length > 0 && (
            <>
              <Divider sx={{ py: 0, my: 0 }}>
                <Typography variant='subtitle2' color="text.secondary">
                  Admin Preferences
                </Typography>
              </Divider>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0, mb: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    Feature Flags
                  </Typography>
                </Stack>
                <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' } }}>
                  {Object.entries(localFeatures).map(([key, value]) => {
                    const label = key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
                    return (
                      <FormControlLabel
                        key={key}
                        label={label}
                        disabled={!isSuperAdmin}
                        control={
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={(e) => handleFeatureChange(key, e.target.checked)}
                            size="small"
                          />
                        }
                        sx={{ m: 0 }}
                      />
                    )
                  })}
                </FormGroup>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
